import type { KitFontId, ThemeTypeStyle } from '../releaseThemes';

import { textWidthEm, wrapWords } from './kitFontMetrics';
import { STORY_FIT_FLOOR, type StoryBox } from './storyLayout';
import type { StoryColours, StoryCss } from './storyStyles';
import { fitWatermarkLockup, type WatermarkForm, type WatermarkLockup } from './watermark';

/**
 * The kit cards: the image assets that are not a Story stack (designs `asset-redesign/g Banners -
 * new`, `i YouTube thumbnail - new`, `m Link preview - new`). Code lays each card out in the frame of
 * its release theme; the plan picks only the cover crop. A card layout is a flat list of positioned
 * items with their look (plain style objects), so the renderer (music24-api `KitCard`), the Studio
 * preview (music24-app `CardPreview`) and the fallback share image of sml-preview draw the same
 * boxes. Every box and size is in the pixels of the file. Pure.
 */

/** The asset types (Studio theme ids) that code lays out as a kit card. */
export const KIT_CARD_ASSET_TYPES: ReadonlyArray<string> = ['banners', 'thumbnail', 'linkPreview'];

/** True for an asset type that code lays out as a kit card. */
export function isKitCardAssetType(assetTypeId: string): boolean {
  return KIT_CARD_ASSET_TYPES.includes(assetTypeId);
}

/** The texts of one card. Code writes them (music24-api `buildKitCard`); the layout places them. */
export type KitCard = KitBannerCard | KitThumbnailCard | KitLinkPreviewCard;

export const KIT_BANNER_SITES = ['youtube', 'x', 'facebook'] as const;
export type KitBannerSite = (typeof KIT_BANNER_SITES)[number];

/** A profile banner (design g): the cover and a text column with the status, the title, the artist and the SmartLink. */
export interface KitBannerCard {
  kind: 'banner';
  site: KitBannerSite;
  title: string;
  artist: string;
  /** "Out Fri 16 Oct" before the release day, "Out now" from it (owner answer 2026-10-08). */
  status: string;
  /** The words before the SmartLink ("Listen on"). */
  linkLead: string;
  /** The SmartLink address with no scheme. Null: no URL line (Facebook, or no published SmartLink). */
  link: string | null;
}

/** A YouTube thumbnail (design i): the cover and a text column with the video label, the title and the artist. */
export interface KitThumbnailCard {
  kind: 'thumbnail';
  title: string;
  artist: string;
  /** The type of the video: "Lyric video", "Visualizer", "Official video", "Official audio". */
  label: string;
}

/** A link preview (design m): "New single", the title and the artist; the cover; the status badge. */
export interface KitLinkPreviewCard {
  kind: 'linkPreview';
  kicker: string;
  title: string;
  artist: string;
  /** The status badge: "Out" / "16 Oct" / "Friday", "Out" / "Now", "Coming" / "Soon". */
  badge: { top: string; value: string; sub: string | null };
}

export type KitCardAlign = 'left' | 'right' | 'center';

/** A text of a card. A chip (a status ring, a label pill) carries its padding and border in `css`; the box is the outer box. */
export interface KitCardText {
  kind: 'text';
  /** What the text is (the test id of the renderer): `status`, `title`, `artist`, `url`, `label`, `kicker`, ... */
  role: string;
  box: StoryBox;
  lines: string[];
  sizePx: number;
  lineHeight: number;
  align: KitCardAlign;
  fontId: KitFontId;
  /** The type (weight, case, letter spacing, italic) and the look (colour, shadow, chip, turn) of the text. */
  css: StoryCss;
  /** The look of each line (an inline span): the Handmade marker under a title. Absent: plain lines. */
  lineCss?: StoryCss;
  /** A part of the first line in a bold weight: the SmartLink after "Listen on". Absent: none. */
  boldTail?: string;
}

/** The cover of a card in the frame of its theme. */
export interface KitCardCover {
  kind: 'cover';
  /** The outer box of the frame (a mat, a plate or a tube included). */
  box: StoryBox;
  outer: StoryCss;
  picture: StoryCss;
  tape: StoryCss | null;
  /** The record behind a Retro sleeve. Null: none. */
  record: StoryCss | null;
}

/** A plain shape of a card: the Noir accent line, the plate of a status badge. */
export interface KitCardShape {
  kind: 'shape';
  role: string;
  box: StoryBox;
  css: StoryCss;
}

/**
 * The Free watermark lockup of a card (design w0, `story/watermark.ts`): the last item of the text
 * column. The box is the lockup box; the renderers draw `watermarkSvg(lockup, tone)` in it.
 */
export interface KitCardWatermark {
  kind: 'watermark';
  box: StoryBox;
  lockup: WatermarkLockup;
}

export type KitCardItem = KitCardText | KitCardCover | KitCardShape | KitCardWatermark;

export interface KitCardLayout {
  /** The size of the file. */
  size: { width: number; height: number };
  /** The ground of the theme over the background colour. Null: a flat background. */
  ground: string | null;
  /** The ornaments of the theme (stripes, arcs, a torn strip): they live outside the content box. */
  deco: StoryCss[];
  items: KitCardItem[];
}

export interface KitCardLayoutInput<C extends KitCard = KitCard> {
  card: C;
  themeId: string;
  fonts: { display: KitFontId; text: KitFontId };
  typeStyle: { display: ThemeTypeStyle & { scale: number }; text: ThemeTypeStyle };
  colors: StoryColours;
  /** Free plans: the Music24 watermark, the last item of the text column. */
  watermark?: boolean;
}

/** A font role of a card: the font and its type style. */
export interface KitCardFont {
  id: KitFontId;
  style: ThemeTypeStyle;
}

export const round2 = (value: number): number => Math.round(value * 100) / 100;

/** The width of a text in px, with the case and the letter spacing of its role. Pure. */
export function cardTextWidth(text: string, font: KitCardFont, sizePx: number): number {
  return textWidthEm(text, font.id, font.style) * sizePx;
}

/** The CSS of a type style: weight, italic, case and letter spacing. */
export function cardTypeCss(style: ThemeTypeStyle): StoryCss {
  return {
    fontWeight: style.weight,
    fontStyle: style.italic ? 'italic' : 'normal',
    textTransform: style.case === 'none' ? 'none' : style.case,
    letterSpacing: `${style.tracking}em`,
  };
}

/**
 * The fit of a card text (`fitText.ts` of the design): a text that is wider than its line shrinks
 * to `floor` of its size; with `maxLines` 2 it then wraps into two lines at that size (or smaller,
 * until the widest line fits). Pure.
 */
export function fitCardText(
  text: string,
  font: KitCardFont,
  sizePx: number,
  widthPx: number,
  options: { maxLines?: 1 | 2; floor?: number } = {}
): { lines: string[]; sizePx: number } {
  const floorShare = options.floor ?? STORY_FIT_FLOOR;
  const full = cardTextWidth(text, font, sizePx);
  if (full <= widthPx || full <= 0) {
    return { lines: [text], sizePx };
  }
  const oneLine = (sizePx * widthPx) / full;
  if ((options.maxLines ?? 1) === 1 || oneLine >= sizePx * floorShare) {
    return { lines: [text], sizePx: Math.floor(oneLine * 100) / 100 };
  }
  const floor = sizePx * floorShare;
  const lines = balancedTwoLines(text, font, floor, widthPx);
  const widest = Math.max(...lines.map((line) => cardTextWidth(line, font, floor)));
  const size = widest > widthPx ? (floor * widthPx) / widest : floor;
  return { lines, sizePx: Math.floor(size * 100) / 100 };
}

/**
 * The text on two lines with the break that makes the wider line the narrowest (CSS `text-wrap:
 * balance`). A text of one word stays on one line. Pure.
 */
export function balancedTwoLines(text: string, font: KitCardFont, sizePx: number, widthPx: number): string[] {
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
  if (words.length < 2) {
    return [text.trim()];
  }
  let best: string[] = [];
  let bestWidth = Number.POSITIVE_INFINITY;
  for (let cut = 1; cut < words.length; cut += 1) {
    const lines = [words.slice(0, cut).join(' '), words.slice(cut).join(' ')];
    const widest = Math.max(...lines.map((line) => cardTextWidth(line, font, sizePx)));
    if (widest < bestWidth) {
      best = lines;
      bestWidth = widest;
    }
  }
  // A text that still needs more than two lines keeps the browser wrap of the first part.
  if (bestWidth > widthPx * 1.5) {
    return wrapWords(text, font.id, font.style, widthPx / sizePx).slice(0, 2);
  }
  return best;
}

/** A text item of a card at a top-left corner, with the size of its lines and of its chip. */
export function cardText(input: {
  role: string;
  x: number;
  y: number;
  lines: string[];
  sizePx: number;
  lineHeight: number;
  font: KitCardFont;
  align: KitCardAlign;
  css: StoryCss;
  /** The chip around the text: padding and border on each side, in px. */
  chip?: { padX: number; padY: number; border: number };
  /** The width of the box. Absent: the widest line (and the chip). */
  width?: number;
  lineCss?: StoryCss;
  boldTail?: string;
}): KitCardText {
  const chip = input.chip ?? { padX: 0, padY: 0, border: 0 };
  const textWidth = Math.max(0, ...input.lines.map((line) => cardTextWidth(line, input.font, input.sizePx)));
  const w = input.width ?? textWidth + 2 * (chip.padX + chip.border);
  const h = input.lines.length * input.sizePx * input.lineHeight + 2 * (chip.padY + chip.border);
  return {
    kind: 'text',
    role: input.role,
    box: { x: round2(input.x), y: round2(input.y), w: round2(w), h: round2(h) },
    lines: input.lines,
    sizePx: input.sizePx,
    lineHeight: input.lineHeight,
    align: input.align,
    fontId: input.font.id,
    css: input.css,
    ...(input.lineCss === undefined ? {} : { lineCss: input.lineCss }),
    ...(input.boldTail === undefined ? {} : { boldTail: input.boldTail }),
  };
}

/** Moves an item by `dx`, `dy`. */
export function shiftCardItem<T extends KitCardItem>(item: T, dx: number, dy: number): T {
  return { ...item, box: { ...item.box, x: round2(item.box.x + dx), y: round2(item.box.y + dy) } };
}

/** The bounding box of items. */
export function cardItemsBounds(items: ReadonlyArray<KitCardItem>): StoryBox {
  if (items.length === 0) {
    return { x: 0, y: 0, w: 0, h: 0 };
  }
  const x0 = Math.min(...items.map((item) => item.box.x));
  const y0 = Math.min(...items.map((item) => item.box.y));
  const x1 = Math.max(...items.map((item) => item.box.x + item.box.w));
  const y1 = Math.max(...items.map((item) => item.box.y + item.box.h));
  return { x: round2(x0), y: round2(y0), w: round2(x1 - x0), h: round2(y1 - y0) };
}

/**
 * The Free watermark lockup of a card at a top-left corner (design w0): the form and the logo
 * height L of the format, shrunk to the logo alone when the column is narrower than the full
 * lockup (`fitWatermarkLockup`, owner rule 2026-10-09). `align: 'right'` puts the right edge at `x`;
 * `center` centres the lockup on `x`.
 */
export function cardWatermark(input: {
  x: number;
  y: number;
  form: WatermarkForm;
  L: number;
  maxWidth: number;
  align: KitCardAlign;
}): KitCardWatermark {
  const lockup = fitWatermarkLockup(input.form, input.L, input.maxWidth);
  let { x } = input;
  if (input.align === 'right') {
    x -= lockup.w;
  } else if (input.align === 'center') {
    x -= lockup.w / 2;
  }
  return { kind: 'watermark', box: { x: round2(x), y: round2(input.y), w: lockup.w, h: lockup.h }, lockup };
}
